<script>
  import { fly } from 'svelte/transition';
  import { onMount } from 'svelte';
  import {
    SMALL,
    MEDIUM,
    LARGE,
    LEFT,
    CENTER,
    RIGHT,
  } from './constants';
  import Motion, { useMotion } from '../Motion';
  import { FAST, MEDIUM as DURATION_MEDIUM } from '../Motion/duration';
  import { entrance, exit, onScreen } from '../Motion/easing';

  const motion = useMotion();

  export let modal;
  $: ({
    size, position, fullscreen, transparent,
  } = modal);

  let container;

  const margin = 40;

  const magnitude = (s) => ({
    [SMALL]: 360 + margin,
    [MEDIUM]: 600 + margin,
    [LARGE]: 900 + margin,
  }[s]);

  const sign = (pos) => ({
    [RIGHT]: 1,
    [CENTER]: 0,
    [LEFT]: -1,
  }[pos]);

  const animationDuration = (pos) => {
    if (motion === Motion.NONE) {
      return 0;
    }
    return {
      RIGHT: DURATION_MEDIUM,
      CENTER: FAST,
      LEFT: DURATION_MEDIUM,
    }[pos];
  };

  $: easingIn = $position === CENTER ? onScreen : entrance;
  $: easingOut = $position === CENTER ? onScreen : exit;
  $: duration = animationDuration($position);
  $: x = sign($position) * magnitude($size);
  $: opacity = $position === CENTER ? 0 : 1;

  onMount(() => {
    container.append(modal.element);
    return () => {
      modal.element.remove();
      modal.dispatch('remove');
    };
  });
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
  class='modal-backdrop'
  class:top={$position === 'top'}
  data-cy='dismiss-modal'
  on:mousedown|self={() => modal.dismiss()}>
  <div
    class='modal-backdrop-background'
    in:fly={{ easing: easingIn, duration }}
    out:fly={{ easing: easingOut, duration }}
    />
  <div
    in:fly={{
      opacity,
      x,
      easing: easingIn,
      duration,
    }}
    out:fly={{
      opacity,
      x,
      easing: easingOut,
      duration,
    }}
    class='modal-container {$size} {$position}'
    class:fullscreen={$fullscreen}
    class:transparent
    bind:this={container}
    on:click|stopPropagation />
</div>

<style lang="postcss">
  .modal-backdrop {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    z-index: var(--Modal--z-index);

    &.top {
      align-items: flex-start;
    }
  }

  .modal-backdrop-background {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background-color: var(--color--backdrop);
    pointer-events: none;
  }

  .modal-container {
    --Modal--margin: var(--spacing--xl);

    box-sizing: border-box;
    border-radius: var(--radius--l);
    background-color: var(--color--default--background);
    z-index: 2;

    &.transparent { background-color: transparent; }

    &.small { width: var(--size--overlay--small); }
    &.medium { width: var(--size--overlay--medium); }
    &.large { width: var(--size--overlay--large); }

    &.center { margin: 0 auto; }
    &.left { margin: 0 auto 0 var(--Modal--margin); }
    &.right { margin: 0 var(--Modal--margin) 0 auto; }

    &.fullscreen {
      width: 100%;
      height: 100%;
      margin: 0;
      border-radius: 0;
    }
  }
</style>
